import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import BrandNav from "../components/BrandNav";
import SocialProofTicker from "../components/SocialProofTicker";
import { api } from "../lib/api";
import { BANDS } from "../lib/palette";
import { useAuth } from "../lib/auth";
import { extractError } from "../lib/format";
import { ArrowRight, BookOpen, ChevronDown, ChevronUp, CreditCard, Sparkle, X } from "lucide-react";
import { toast } from "sonner";

const READER_SAMPLE_PDF = "/samples/age_8-9_term_1_chapter_1_reader.pdf";
const READER_THUMBS = [
  { src: "/samples/thumbs/age_8-9_term_1_chapter_1_spread_a.jpg", alt: "Warm illustrated opener page" },
  { src: "/samples/thumbs/age_8-9_term_1_chapter_1_spread_b.jpg", alt: "Worked example and Wonder Moment" },
  { src: "/samples/thumbs/age_8-9_term_1_chapter_1_spread_c.jpg", alt: "Hands-on Try This At Home activity" },
];

// Pricing tiers as a data table. Marketing copy (names, notes, features,
// installment labels) is resolved via i18n keys at render time — see
// `renderPricingCard` below. Only stable, non-translatable identifiers
// (tier_code, price, action, featured) live here.
const PRICING = [
  { key: "freePreview", price: "£0",   action: "start_free" },
  {
    key: "level1",
    tier_code: "level_1_full",
    price: "£80",
    installmentCodes: [
      "level_1_installment_1",
      "level_1_installment_2",
      "level_1_installment_3",
    ],
  },
  { key: "level2", tier_code: "level_2_upgrade", price: "£100" },
  { key: "bundle", tier_code: "bundle", price: "£175", featured: true },
];

export default function Landing() {
  const nav = useNavigate();
  const { t } = useTranslation("common");
  const { user } = useAuth();
  const [counts, setCounts] = useState({});

  useEffect(() => {
    let alive = true;
    api
      .get("/catalog/age-bands")
      .then(({ data }) => {
        if (!alive) return;
        const m = {};
        for (const b of data.age_bands) m[b.id] = b.lesson_count;
        setCounts(m);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const goStart = () => {
    if (user && user !== false) {
      nav("/pick-band");
    } else {
      nav("/signup");
    }
  };

  return (
    <div data-testid="landing-page">
      <BrandNav />
      <main className="ftg-shell ftg-page">
        <section className="ftg-hero">
          <span className="ftg-hero__eyebrow" data-testid="hero-eyebrow">
            {t("hero.eyebrow")}
          </span>
          <h1 className="ftg-hero__title" data-testid="hero-title">
            {t("hero.titleBefore")}<em>STEAM</em>{t("hero.titleAfter")}
          </h1>
          <p className="ftg-hero__sub" data-testid="hero-sub">
            {t("hero.sub")}
          </p>
          <div className="ftg-hero__ctas">
            <button
              onClick={goStart}
              className="ftg-btn ftg-btn--primary ftg-btn--large"
              data-testid="hero-start-btn"
            >
              {t("hero.startFree")} <ArrowRight size={18} />
            </button>
            <a href="#pricing" className="ftg-btn ftg-btn--ghost ftg-btn--large" data-testid="hero-pricing-link">
              {t("hero.seeIncluded")}
            </a>
          </div>
        </section>

        <section className="ftg-bands" data-testid="age-bands-section">
          <h2 className="ftg-section-title">{t("bands.sectionTitle")}</h2>
          <p className="ftg-section-sub">{t("bands.sectionSub")}</p>
          <div className="ftg-bands__grid">
            {BANDS.map(({ id, gradient, Icon, accent }) => (
              <article
                key={id}
                className="ftg-band-card"
                style={{
                  background: `linear-gradient(140deg, ${gradient[0]} 0%, ${gradient[1]} 100%)`,
                }}
                data-testid={`band-card-${id}`}
              >
                <div>
                  <span className="ftg-band-card__icon" style={{ color: accent }}>
                    <Icon size={22} strokeWidth={2} />
                  </span>
                  <h3 className="ftg-band-card__label">{t(`bands.${id}.label`)}</h3>
                  <p className="ftg-band-card__tag">{t(`bands.${id}.tagline`)}</p>
                </div>
                <div className="ftg-band-card__count" data-testid={`band-count-${id}`}>
                  {t("bands.lessonsTerms", { count: counts[id] ?? 60 })}
                </div>
              </article>
            ))}
          </div>
        </section>

        <section id="pricing" className="ftg-pricing" data-testid="pricing-section">
          <h2 className="ftg-section-title">{t("pricing.title")}</h2>
          <p className="ftg-section-sub">{t("pricing.sub")}</p>
          <div className="ftg-pricing__grid">
            {PRICING.map((p) => {
              const name = t(`pricing.${p.key}.name`);
              const note = t(`pricing.${p.key}.note`);
              const sub  = p.key === "freePreview" ? t("pricing.freePreview.sub") : null;
              const features = t(`pricing.${p.key}.features`, { returnObjects: true, defaultValue: [] });
              const testSlug = name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
              return (
                <div
                  key={p.key}
                  className={`ftg-price-card${p.featured ? " ftg-price-card--featured" : ""}`}
                  data-testid={`pricing-card-${p.key}`}
                >
                  <div className="ftg-price-card__name">{name}</div>
                  <div className="ftg-price-card__price">{p.price}</div>
                  <div className="ftg-price-card__note">{note}</div>
                  {sub && (
                    <div className="ftg-price-card__sub" data-testid={`pricing-sub-${p.key}`}>
                      {sub}
                    </div>
                  )}
                  {Array.isArray(features) && features.length > 0 && (
                    <ul className="ftg-price-card__features" data-testid={`pricing-features-${p.key}`}>
                      {features.map((f) => (
                        <li key={f} className="ftg-price-card__feature">{f}</li>
                      ))}
                    </ul>
                  )}
                  {p.action === "start_free" && (
                    <button
                      type="button"
                      onClick={goStart}
                      className="ftg-btn ftg-btn--ghost ftg-btn--sm"
                      data-testid="pricing-start-free"
                    >
                      {t("pricing.freePreview.cta")} <ArrowRight size={14} />
                    </button>
                  )}
                  {p.tier_code && (
                    <BuyButton tier_code={p.tier_code} />
                  )}
                  {p.installmentCodes && (
                    <div className="ftg-price-card__installments" data-testid="pricing-installments">
                      <div className="ftg-price-card__installments-hd">
                        {t("pricing.level1.installmentsHeading")}
                      </div>
                      {p.installmentCodes.map((code, i) => (
                        <BuyButton
                          key={code}
                          tier_code={code}
                          label={t(`pricing.level1.install${i + 1}`)}
                          small
                        />
                      ))}
                    </div>
                  )}
                  {(p.tier_code || p.action === "start_free") && (
                    <WhatsInsideReveal cardName={name} testSlug={testSlug} />
                  )}
                </div>
              );
            })}
          </div>

          <FoundersCohort />
        </section>

        <LaunchWaitlist />

        <footer className="ftg-foot" data-testid="landing-footer">
          {t("footer.line", { year: new Date().getFullYear() })}
        </footer>
      </main>
    </div>
  );
}

function BuyButton({ tier_code, label, small = false }) {
  const nav = useNavigate();
  const { t } = useTranslation("common");
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const resolvedLabel = label || t("pricing.buyNow");

  const click = async () => {
    if (!user) {
      try { sessionStorage.setItem("ftg_pending_tier", tier_code); } catch { /* no-op */ }
      nav("/signup");
      return;
    }
    setBusy(true);
    try {
      const { data: mine } = await api.get("/enrollments/me").catch(() => ({ data: { enrollments: [] } }));
      const age_band = mine.enrollments?.[0]?.age_band;
      if (!age_band) {
        nav(`/pick-band?next=checkout&tier=${encodeURIComponent(tier_code)}`);
        setBusy(false);
        return;
      }
      const { data } = await api.post("/checkout/session", {
        tier_code,
        age_band,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) {
        toast.error(e.response.data?.detail || "Founder's cohort is full.");
      } else {
        toast.error(extractError(e, "Could not start checkout. Please try again."));
      }
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={click}
      disabled={busy}
      className={`ftg-btn ${small ? "ftg-btn--ghost ftg-btn--sm" : "ftg-btn--secondary"}`}
      data-testid={`buy-${tier_code}`}
    >
      {busy ? t("pricing.opening") : (
        <>
          {!small && <CreditCard size={14} />} {resolvedLabel}
        </>
      )}
    </button>
  );
}

function WhatsInsideReveal({ cardName, testSlug }) {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const bullets = t("pricing.readerBullets", { returnObjects: true, defaultValue: [] });

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      api.post("/marketing/event", {
        event_type: "pricing_reveal_expanded",
        referrer: cardName,
      }).catch(() => { /* fire and forget */ });
    }
  };

  const onSample = () => {
    api.post("/marketing/event", {
      event_type: "reader_sample_downloaded",
      referrer: cardName,
    }).catch(() => { /* fire and forget */ });
    window.open(READER_SAMPLE_PDF, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="ftg-inside" data-testid={`inside-${testSlug}`}>
      <button
        type="button"
        onClick={toggle}
        className="ftg-inside__toggle"
        aria-expanded={open}
        data-testid={`inside-toggle-${testSlug}`}
      >
        {t("pricing.seeInside")} {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>

      {open && (
        <div className="ftg-inside__body" data-testid={`inside-body-${testSlug}`}>
          <div className="ftg-inside__thumbs" data-testid={`inside-thumbs-${testSlug}`}>
            {READER_THUMBS.map((th) => (
              <button
                key={th.src}
                type="button"
                className="ftg-inside__thumb"
                onClick={() => setLightbox(th)}
                aria-label={`Preview ${th.alt}`}
                data-testid={`inside-thumb-${testSlug}-${th.src.split("_").pop().replace(".jpg","")}`}
              >
                <img src={th.src} alt={th.alt} loading="lazy" />
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={onSample}
            className="ftg-btn ftg-btn--ghost ftg-btn--sm ftg-inside__sample-btn"
            data-testid={`inside-sample-${testSlug}`}
          >
            <BookOpen size={14} /> {t("pricing.previewChapter")}
          </button>

          <ul className="ftg-inside__bullets">
            {Array.isArray(bullets) && bullets.map((b) => (
              <li key={b} className="ftg-inside__bullet">
                <span className="ftg-inside__bullet__dot" aria-hidden="true" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {lightbox && (
        <div
          className="ftg-inside__lightbox"
          onClick={() => setLightbox(null)}
          role="dialog"
          aria-modal="true"
          data-testid={`inside-lightbox-${testSlug}`}
        >
          <img src={lightbox.src} alt={lightbox.alt} />
          <button
            type="button"
            className="ftg-inside__lightbox__close"
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            aria-label="Close preview"
          >
            <X size={20} />
          </button>
        </div>
      )}
    </div>
  );
}

function FoundersCohort() {
  const { t } = useTranslation("common");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [seats, setSeats] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    let alive = true;
    api.get("/enrollments/founder-count")
      .then(({ data }) => { if (alive) setSeats(data); })
      .catch(() => { /* silent */ });
    return () => { alive = false; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/waitlist", {
        email,
        source: "founders_cohort",
        preferred_tier: "founder",
      });
      setDone(true);
      toast.success(data.message || "You're on the list.");
    } catch (err) {
      toast.error(extractError(err, "Could not join list."));
    } finally {
      setBusy(false);
    }
  };

  const soldOut = seats && seats.remaining <= 0;
  const seatsCopy = seats
    ? (soldOut
        ? t("founders.seatsSoldOut")
        : t("founders.seatsClaimed", { claimed: seats.claimed, total: seats.total, remaining: seats.remaining }))
    : t("founders.seatsInitial");

  return (
    <div className="ftg-founders" data-testid="founders-cohort">
      <div className="ftg-founders__badge">
        <Sparkle size={14} /> {soldOut ? t("founders.badgeSoldOut") : t("founders.badgeLimited")}
      </div>
      <h3 className="ftg-founders__title">{t("founders.title")}</h3>
      <p className="ftg-founders__note">{t("founders.note")}</p>
      <div className="ftg-founders__seats" data-testid="founders-seats">{seatsCopy}</div>
      <div className="ftg-founders__proof-row">
        <SocialProofTicker />
      </div>

      {!soldOut && (
        <div className="ftg-founders__actions">
          <BuyButton tier_code="founder" label={user ? t("founders.claimCTA") : t("founders.signupClaim")} />
        </div>
      )}

      {done ? (
        <div className="ftg-founders__done" data-testid="founders-done">
          {t("founders.done")}
        </div>
      ) : (
        <form className="ftg-founders__form" onSubmit={submit} data-testid="founders-form">
          <input
            type="email"
            required
            placeholder={soldOut ? t("founders.emailPlaceholderSoldOut") : t("founders.emailPlaceholder")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="ftg-field__input"
            data-testid="founders-email-input"
          />
          <button
            type="submit"
            disabled={busy}
            className="ftg-btn ftg-btn--ghost"
            data-testid="founders-submit-btn"
          >
            {busy ? t("founders.joining") : (soldOut ? t("founders.joinLaunchList") : t("founders.joinList"))}
          </button>
        </form>
      )}
    </div>
  );
}

function LaunchWaitlist() {
  const { t } = useTranslation("common");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/waitlist", {
        email,
        source: "general_launch",
        preferred_tier: "any",
      });
      setDone(true);
      toast.success(data.message || "You're on the list.");
    } catch (err) {
      toast.error(extractError(err, "Could not join list."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="ftg-launch" data-testid="launch-waitlist">
      <div className="ftg-launch__inner">
        <div>
          <h3 className="ftg-launch__title">{t("waitlist.title")}</h3>
          <p className="ftg-launch__sub">{t("waitlist.sub")}</p>
        </div>
        {done ? (
          <div className="ftg-launch__done" data-testid="launch-done">
            {t("waitlist.done")}
          </div>
        ) : (
          <form className="ftg-launch__form" onSubmit={submit} data-testid="launch-form">
            <input
              type="email"
              required
              placeholder={t("waitlist.emailPlaceholder")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="ftg-field__input"
              data-testid="launch-email-input"
            />
            <button
              type="submit"
              disabled={busy}
              className="ftg-btn ftg-btn--primary"
              data-testid="launch-submit-btn"
            >
              {busy ? t("waitlist.adding") : t("waitlist.notifyMe")}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
